import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaHome,
  FaUser,
  FaUsers,
  FaBook,
  FaShoppingBag,
  FaClipboardCheck,
  FaFileInvoiceDollar,
  FaMoneyCheckAlt,
  FaChalkboardTeacher,
  FaMoneyBillWave
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { authAPI, studentAPI, teacherAPI, productAPI, invoiceAPI, paymentAPI, getImageUrl } from '../services/api';
import { formatApiError } from '../utils/errorUtils';
import DashboardLayout from '../components/DashboardLayout';
import StatCardGrid from '../components/StatCardGrid';
import StudentsPage from './StudentsPage';
import TeachersPage from './TeachersPage';
import BatchesPage from './BatchesPage';
import ProductsPage from './ProductsPage';
import AttendancePage from './AttendancePage';
import EnrollmentsPage from './EnrollmentsPage';
import InvoicesPage from './InvoicesPage';
import PaymentsPage from './PaymentsPage';
import './TeacherDashboard.css';

const buildInvoiceSummaryFallback = (rows = []) =>
  rows.reduce(
    (acc, item) => {
      const status = String(item.ComputedStatus || item.Status || 'Pending').toLowerCase();
      acc.totalInvoices += 1;
      acc.totalAmount += Number(item.TotalAmount || 0);
      acc.statusCounts[status] = (acc.statusCounts[status] || 0) + 1;
      return acc;
    },
    {
      totalInvoices: 0,
      totalAmount: 0,
      statusCounts: { paid: 0, partial: 0, pending: 0, cancelled: 0 }
    }
  );

const buildPaymentSummaryFallback = (rows = []) =>
  rows.reduce(
    (acc, item) => {
      acc.totalPayments += 1;
      acc.totalReceived += Number(item.AmountPaid || 0);
      return acc;
    },
    { totalPayments: 0, totalReceived: 0 }
  );

function TeacherDashboard() {
  const { user, logout, updateStoredUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.focusTab || 'dashboard');
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [dashboardMetrics, setDashboardMetrics] = useState({
    students: 0,
    teachers: 0,
    products: 0,
    invoices: 0,
    paidInvoices: 0,
    pendingInvoices: 0,
    totalReceived: 0,
    totalBilled: 0
  });
  const [profileForm, setProfileForm] = useState({
    Email: '',
    FirstName: '',
    LastName: '',
    ContactNumber: '',
    Specialization: '',
    PhotoPath: ''
  });
  const [profileMessage, setProfileMessage] = useState(
    location.state?.showProfileNotice ? 'Registration completed. Please update the teacher profile now.' : ''
  );
  const [profileError, setProfileError] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  useEffect(() => {
    const loadDashboard = async () => {
      setDashboardLoading(true);
      try {
        const [profileResponse, studentsResponse, teachersResponse, productsResponse, invoicesResponse, paymentsResponse] = await Promise.all([
          authAPI.getTeacherProfile(),
          studentAPI.getAll(),
          teacherAPI.getAll(),
          productAPI.getAll(),
          invoiceAPI.getAll(),
          paymentAPI.getAll()
        ]);

        const nextProfile = profileResponse.data.teacher || {};
        const invoiceRows = invoicesResponse.data || [];
        const paymentRows = paymentsResponse.data || [];
        let invoiceSummary = buildInvoiceSummaryFallback(invoiceRows);
        let paymentSummary = buildPaymentSummaryFallback(paymentRows);

        try {
          const [invoiceSummaryResponse, paymentSummaryResponse] = await Promise.all([
            invoiceAPI.getSummary(),
            paymentAPI.getSummary()
          ]);
          invoiceSummary = invoiceSummaryResponse.data || invoiceSummary;
          paymentSummary = paymentSummaryResponse.data || paymentSummary;
        } catch (summaryError) {
          if (summaryError?.response?.status !== 404) {
            throw summaryError;
          }
        }

        setProfile(nextProfile);
        setProfileForm({
          Email: nextProfile.Email || '',
          FirstName: nextProfile.FirstName || '',
          LastName: nextProfile.LastName || '',
          ContactNumber: nextProfile.ContactNumber || '',
          Specialization: nextProfile.Specialization || '',
          PhotoPath: nextProfile.PhotoPath || ''
        });
        setDashboardMetrics({
          students: (studentsResponse.data || []).length,
          teachers: (teachersResponse.data || []).length,
          products: (productsResponse.data || []).length,
          invoices: invoiceSummary.totalInvoices || 0,
          paidInvoices: invoiceSummary.statusCounts?.paid || 0,
          pendingInvoices: (invoiceSummary.statusCounts?.pending || 0) + (invoiceSummary.statusCounts?.partial || 0),
          totalReceived: paymentSummary.totalReceived || 0,
          totalBilled: invoiceSummary.totalAmount || 0
        });
        setProfileError('');
      } catch (err) {
        setProfileError(formatApiError(err, 'Failed to load staff profile'));
      } finally {
        setDashboardLoading(false);
      }
    };

    if (user?.TeacherID) {
      loadDashboard();
    } else {
      setDashboardLoading(false);
    }
  }, [user?.TeacherID]);

  const teacherData = profile || user || {};
  const teacherName = teacherData?.FirstName ? `${teacherData.FirstName} ${teacherData.LastName || ''}`.trim() : 'Staff Member';
  const rawRole = String(teacherData?.Role || teacherData?.role || user?.Role || user?.role || user?.userType || '').toLowerCase();
  const staffRoleLabel = rawRole === 'super_admin' ? 'Super Admin' : rawRole === 'admin' ? 'Admin' : rawRole === 'teacher' ? 'Teacher' : (isAdmin ? 'Admin' : 'Teacher');

  const navLinks = useMemo(
    () => [
      { id: 'dashboard', label: 'Dashboard', icon: FaHome },
      { id: 'students', label: 'Users', icon: FaUsers, badge: dashboardMetrics.students },
      { id: 'teachers', label: 'Teachers', icon: FaChalkboardTeacher, badge: dashboardMetrics.teachers },
      { id: 'batches', label: 'Batches', icon: FaBook },
      { id: 'products', label: 'Products', icon: FaShoppingBag, badge: dashboardMetrics.products },
      { id: 'enrollments', label: 'Enrollments', icon: FaClipboardCheck },
      { id: 'attendance', label: 'Attendance', icon: FaClipboardCheck },
      { id: 'invoices', label: 'Invoices', icon: FaFileInvoiceDollar, badge: dashboardMetrics.invoices },
      { id: 'payments', label: 'Payments', icon: FaMoneyCheckAlt },
      { id: 'profile', label: 'My Profile', icon: FaUser }
    ],
    [dashboardMetrics]
  );

  const quickStats = useMemo(
    () => [
      { id: 'users', label: 'Users', value: dashboardMetrics.students, icon: FaUsers, color: 'blue', onClick: () => setActiveTab('students') },
      { id: 'teachers', label: 'Teachers', value: dashboardMetrics.teachers, icon: FaChalkboardTeacher, color: 'indigo', onClick: () => setActiveTab('teachers') },
      { id: 'products', label: 'Products', value: dashboardMetrics.products, icon: FaShoppingBag, color: 'purple', onClick: () => setActiveTab('products') },
      { id: 'invoices', label: 'Invoices', value: dashboardMetrics.invoices, icon: FaFileInvoiceDollar, color: 'emerald', onClick: () => setActiveTab('invoices') },
      { id: 'pending', label: 'Pending Billing', value: dashboardMetrics.pendingInvoices, icon: FaClipboardCheck, color: 'amber', onClick: () => setActiveTab('invoices') },
      { id: 'payments', label: 'Payments Received', value: `Rs ${Number(dashboardMetrics.totalReceived || 0).toFixed(2)}`, icon: FaMoneyBillWave, color: 'rose', onClick: () => setActiveTab('payments') }
    ],
    [dashboardMetrics]
  );

  const handleProfileInputChange = (e) => {
    const { name, value, type, files } = e.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: type === 'file' && files?.length ? files[0] : value
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileError('');
    setProfileMessage('');

    try {
      const data = new FormData();
      Object.entries(profileForm).forEach(([key, value]) => {
        if (key === 'PhotoPath') {
          if (value instanceof File) {
            data.append(key, value);
          }
          return;
        }
        data.append(key, value);
      });

      const response = await authAPI.updateTeacherProfile(data);
      const nextProfile = response.data.teacher;
      setProfile(nextProfile);
      updateStoredUser({
        ...user,
        Email: nextProfile.Email,
        FirstName: nextProfile.FirstName,
        LastName: nextProfile.LastName,
        ContactNumber: nextProfile.ContactNumber,
        Specialization: nextProfile.Specialization,
        PhotoPath: nextProfile.PhotoPath,
        IsAdmin: nextProfile.IsAdmin,
        Role: nextProfile.Role
      });
      setProfileForm((prev) => ({ ...prev, PhotoPath: nextProfile.PhotoPath || prev.PhotoPath }));
      setProfileMessage('Teacher profile updated successfully.');
    } catch (err) {
      setProfileError(formatApiError(err, 'Failed to update staff profile'));
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <DashboardLayout
      brandName="Epsilon Skating Club"
      userProfile={{
        name: teacherName,
        email: teacherData?.Email || user?.Email || '',
        avatar: teacherData?.PhotoPath ? getImageUrl(teacherData.PhotoPath) : null
      }}
      roleBadge={staffRoleLabel}
      navLinks={navLinks}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onLogout={handleLogout}
      headerTitle={activeTab === 'dashboard' ? 'Staff & Admin Dashboard' : navLinks.find(l => l.id === activeTab)?.label || 'Dashboard'}
      headerSubtitle={activeTab === 'dashboard' ? 'Manage club members, batches, financial billing, attendance, and store inventory.' : ''}
    >
      {profileError && <div className="alert alert-error">{profileError}</div>}
      {profileMessage && <div className="alert alert-success">{profileMessage}</div>}

      {dashboardLoading ? (
        <div className="loading loading-panel">Loading staff dashboard...</div>
      ) : (
        <>
          {/* MAIN DASHBOARD OVERVIEW TAB (Stat cards ONLY shown here!) */}
          {activeTab === 'dashboard' && (
            <div className="tdb-dashboard-tab">
              <div className="mb-6">
                <StatCardGrid stats={quickStats} loading={dashboardLoading} layoutMode="grid" />
              </div>

              <section className="tdb-summary-strip mb-6">
                <article><span>Paid Invoices</span><strong>{dashboardMetrics.paidInvoices}</strong></article>
                <article><span>Pending / Partial</span><strong>{dashboardMetrics.pendingInvoices}</strong></article>
                <article><span>Total Billed</span><strong>{`Rs ${Number(dashboardMetrics.totalBilled || 0).toFixed(2)}`}</strong></article>
                <article><span>Total Received</span><strong>{`Rs ${Number(dashboardMetrics.totalReceived || 0).toFixed(2)}`}</strong></article>
              </section>

              <div className="quick-start-section container mt-4">
                <h3 className="page-title text-xl mb-3">Quick Navigation</h3>
                <div className="quick-links">
                  <button type="button" className="quick-link" onClick={() => setActiveTab('students')}>
                    <FaUsers /> Manage Users ({dashboardMetrics.students})
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('teachers')}>
                    <FaChalkboardTeacher /> Manage Teachers ({dashboardMetrics.teachers})
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('batches')}>
                    <FaBook /> Manage Batches
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('products')}>
                    <FaShoppingBag /> Club Products ({dashboardMetrics.products})
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('enrollments')}>
                    <FaClipboardCheck /> Student Enrollments
                  </button>
                  <button type="button" className="quick-link" onClick={() => setActiveTab('invoices')}>
                    <FaFileInvoiceDollar /> Manage Invoices ({dashboardMetrics.invoices})
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <section className="tdb-profile-card">
              <div className="tdb-section-header">
                <h2>{staffRoleLabel} Profile</h2>
                <p>Update contact details, specialization, and profile image for the teacher workspace.</p>
              </div>

              <div className="tdb-profile-layout">
                <div className="tdb-profile-column">
                  <section className="tdb-section-card">
                    <div className="tdb-profile-summary">
                      <article><label>Teacher ID</label><p>{teacherData?.TeacherID || 'N/A'}</p></article>
                      <article><label>Role</label><p>{teacherData?.Role || staffRoleLabel.toLowerCase()}</p></article>
                      <article><label>Email</label><p>{teacherData?.Email || 'N/A'}</p></article>
                      <article><label>Phone</label><p>{teacherData?.ContactNumber || 'N/A'}</p></article>
                    </div>
                  </section>
                </div>

                <div className="tdb-profile-column tdb-profile-editor">
                  <section className="tdb-section-card">
                    <div className="tdb-profile-photo tdb-photo-card">
                      <p>Profile Photo</p>
                      {teacherData?.PhotoPath ? (
                        <img src={getImageUrl(teacherData.PhotoPath)} alt={teacherName} />
                      ) : (
                        <div className="tdb-photo-placeholder">
                          <FaUser />
                          <span>No profile image yet</span>
                        </div>
                      )}
                    </div>

                    <form onSubmit={handleProfileSubmit} className="tdb-profile-form">
                      <div className="form-row">
                        <div className="form-group"><label>Email</label><input type="email" name="Email" value={profileForm.Email} onChange={handleProfileInputChange} required /></div>
                        <div className="form-group"><label>Contact Number</label><input type="text" name="ContactNumber" value={profileForm.ContactNumber} onChange={handleProfileInputChange} /></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>First Name</label><input type="text" name="FirstName" value={profileForm.FirstName} onChange={handleProfileInputChange} /></div>
                        <div className="form-group"><label>Last Name</label><input type="text" name="LastName" value={profileForm.LastName} onChange={handleProfileInputChange} /></div>
                      </div>
                      <div className="form-row">
                        <div className="form-group"><label>Specialization</label><input type="text" name="Specialization" value={profileForm.Specialization} onChange={handleProfileInputChange} /></div>
                        <div className="form-group">
                          <label>Profile Image</label>
                          <input type="file" name="PhotoPath" onChange={handleProfileInputChange} accept="image/*" />
                          <p className="form-help">Use a square JPG or PNG image for the cleanest avatar display.</p>
                          {profileForm.PhotoPath && (
                            <div className="upload-preview">
                              <img
                                src={profileForm.PhotoPath instanceof File ? URL.createObjectURL(profileForm.PhotoPath) : getImageUrl(profileForm.PhotoPath)}
                                alt="Teacher preview"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={savingProfile}>{savingProfile ? 'Saving...' : 'Save Profile'}</button></div>
                    </form>
                  </section>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'students' && <StudentsPage />}
          {activeTab === 'teachers' && <TeachersPage />}
          {activeTab === 'batches' && <BatchesPage />}
          {activeTab === 'products' && <ProductsPage />}
          {activeTab === 'enrollments' && <EnrollmentsPage />}
          {activeTab === 'attendance' && <AttendancePage />}
          {activeTab === 'invoices' && <InvoicesPage />}
          {activeTab === 'payments' && <PaymentsPage />}
        </>
      )}
    </DashboardLayout>
  );
}

export default TeacherDashboard;
